/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell.�*/
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import forgeIcon from "./Forge Icon.png";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Button, Paper, Tooltip } from "@mui/material";
import CreateTenantPage from "../CreateTenant/CreateTenantPage";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DisabledByDefaultOutlinedIcon from "@mui/icons-material/DisabledByDefaultOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import Home from "../../pages/Home/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import { color } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import { AppRoutes } from "../../AppRoutes";
import { FC, useEffect, useRef, useState } from "react";
import React from "react";
import { OidcSecure, useOidc } from "@axa-fr/react-oidc";
import { ConfigurationVariables } from "../../config";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  getAccessToken,
  getAvailableCountries,
  getAvailableCurrencies,
  getAvailableDataCenters,
  getAvailableProducts,
  getAvailableRegions,
  getAvailableTimeZones,
  getForgeCustomeIdTenantData,
  getTenantFromToken,
  getTenantList,
} from "../../store/Tenant/tenant-actions";

import Loader from "../Loader/Loader";
import { getUserPermissions } from "../../store/User/user-actions";
import UserPermissions from "../../models/apiModels/UserPermissions";
import UserIcon from "../../images/User.png";
import ToastBarForError from "../SharedComponents/ToastBarForError";
import { IErrorProps } from "../../models";

const drawerWidth = 300;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  backgroundColor: "#000000",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  backgroundColor: "#000000",

  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(5)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const NavigateBar = () => {
  const theme = useTheme();
  const location = useLocation();
  const [pathName, setPathName] = useState(location?.pathname);
  const [open, setOpen] = React.useState(false);
  const history = useNavigate();

  const [activeMenuItem, setActiveMenuItem] = useState("");
  const activeIconColorFilter =
    "invert(76%) sepia(72%) saturate(2442%) hue-rotate(176deg) brightness(101%) contrast(102%)";

  const drawerItems = [
    {
      name: "Home",
      label: (
        <span
          style={{
            fontSize: ".8rem",
            fontFamily: "Honeywell Sans Web",
            fontWeight: "800",
          }}
        >
          Home{" "}
        </span>
      ),
      icon: (
        <Tooltip title="Home" placement="bottom-start">
          <HomeOutlinedIcon
            color={
              activeMenuItem === "Home" ||
              location?.pathname.includes("/tenant")
                ? "primary"
                : "inherit"
            }
          />
        </Tooltip>
      ),
      component: () => {
        setActiveMenuItem("Home");
        if (userPermissions.can_read_tenant) {
          return history("/tenant");
        } else {
          const error: IErrorProps = {
            errorCode: 403,
            errorMessage: "User doesn't have privileges to List the Tenants",
          };
          const errorListData: IErrorProps[] = [];
          errorListData.push(error);
          toastErrors.current = errorListData;
          setOpenToastError(true);
        }
      },
    },
  
    {
      name: "HoneywellUser",
      label: (
        <span
          style={{
            fontSize: ".8rem",
            fontFamily: "Honeywell Sans Web",
            fontWeight: "800",
          }}
        >
          Honeywell User Management{" "}
        </span>
      ),
      icon: (
        <Tooltip title="Honeywell Users" placement="bottom-start">
          <img
            src={UserIcon}
            style={{
              filter:
                activeMenuItem === "HoneywellUser" ||
                location?.pathname.includes("/honeywell/user")
                  ? activeIconColorFilter
                  : "inherit",
            }}
          />
        </Tooltip>
      ),
      component: () => {
        if (userPermissions.can_read_honeywell_user) {
          setActiveMenuItem("HoneywellUser");
          return history("/honeywell/user");
        } else {
          const error: IErrorProps = {
            errorCode: 403,
            errorMessage:
              "User doesn't have privileges to List the Honeywell Users",
          };
          const errorListData: IErrorProps[] = [];
          errorListData.push(error);
          toastErrors.current = errorListData;
          setOpenToastError(true);
        }
      },
    },

  ];

  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState<object>();
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleUserSignout = () => {
    logout(ConfigurationVariables.KONG_URL + "logout");
  };
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [openToastError, setOpenToastError] = useState(false);
  const toastErrors = useRef<IErrorProps[]>([]);
  const userPermissionFetched = useSelector(
    (state: RootState) => state.user.isUserPermissionsFetched
  );

  const userPermissions: UserPermissions = useSelector(
    (state: RootState) => state.user.userPermissions
  );

  const handleToastBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToastError(false);
  };

  useEffect(() => {
    const tokenData = getTenantFromToken();

    setIsLoading(true);
    //If user permissions are not fetched Fetch the permissions
    //For Logged in User before performing any action
    if (!userPermissionFetched) {
      dispatch(getUserPermissions())
        .unwrap()
        .then((response) => {
          const userPermissionsResponse: UserPermissions = response.result;
          //If the user is tenant admin then redirecting him to tenant user page
          if (
            !userPermissionsResponse.can_read_tenant &&
            userPermissionsResponse.can_read_user
          ) {
            dispatch(getForgeCustomeIdTenantData())
              .unwrap()
              .then((response) => {
                setIsLoading(true);
                navigate("/tenant/user", {
                  state: response,
                });
              })
              .catch(() => {})
              .finally(() => setIsLoading(false));
          } else if (userPermissionsResponse.can_read_tenant) {
            navigate("/tenant");
          } else {
            navigate("/error");
          }
        })
        .catch((err: any) => {
          let errorCode = err.status;
          if (errorCode == 401) {
            navigate("/error");
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  const { logout } = useOidc();

  return (
    <Box
      sx={{
        display: "flex",
        marginTop: "5vh",
        height: "92vh",
      }}
    >
      {" "}
      {/**For the other pages height*/}
      <AppBar
        position="fixed"
        open={open}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          height: "0px",
          backgroundColor: "#000000",
          minHeight: "6vh", //back bar behind header
        }}
      >
        <Toolbar
          sx={{ backgroundColor: "#000000", height: 0, paddingRight: 0 }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon sx={{ fontSize: "1.5rem" }} />
          </IconButton>
          <Box>
            {!open && <Typography
              sx={{
                "&.MuiTypography-root": {
                  fontFamily: "Honeywell Sans Web",
                  color: "#F0F0F0",
                  fontWeight: 900,
                  fontSize: "1.1rem",
                  display: "flex",
                },
              }}
              noWrap
              component="div"
            >
              HONEYWELL FORGE
            </Typography>}
          </Box>
          <Box
            sx={{
              marginLeft: "auto",
              alignItems: "flex-end",
              textAlign: "flex-end",
            }}
          >
            <div />
            {
              <Tooltip title="Logout">
                <IconButton
                  color="inherit"
                  onClick={handleUserSignout}
                  edge="end"
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            }
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader style={{ justifyContent: "flex-start", minHeight: "6vh"}}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon sx={{ color: "white" }} />
            ) : (
              <ChevronLeftIcon sx={{ color: "white" }} />
            )}
          </IconButton>
          <Typography
              sx={{
                "&.MuiTypography-root": {
                  fontFamily: "Honeywell Sans Web",
                  color: "#F0F0F0",
                  fontWeight: 900,
                  fontSize: "1.1rem",
                  display: "flex",
                },
              }}
              noWrap
              component="div"
            >
              HONEYWELL FORGE
            </Typography>
        </DrawerHeader>
        <Divider />
        <List>
          {drawerItems.map((item) => (
            <ListItem key={item.name} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={item.component}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2,
                  backgroundColor:
                    item.name === activeMenuItem ? "#103F61" : "#000000",
                }}
              >
                {item.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "#F0F0F0",
                    }}
                  >
                    {" "}
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <List
          sx={{
            position: "absolute",
            bottom: "1px",
            minWidth: 0,
            mr: open ? 3 : "auto",
            justifyContent: "center",
            margin: "7px",
            backgroundColor: "black",
          }}
        >
          <div style={{ position: "relative", margin: "3px" }}>
            {" "}
            <img
              className="image-sizing-primary"
              src={forgeIcon}
              alt="shipImage"
            />
          </div>
        </List>
      </Drawer>
      <AppRoutes />
      {openToastError && !isLoading && (
        <ToastBarForError
          open={openToastError}
          onClose={handleToastBarClose}
          errorList={toastErrors.current}
        />
      )}
      {isLoading && <Loader />}
    </Box>
  );
};
